import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  X,
  Printer,
  MapPin,
  Package,
  Loader2,
  ClipboardList,
  ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { ShopifyOrder } from '../../types/shopify';
import type { ProductBinLocation } from '../../types/warehouse';
import { preloadPickListImages } from '../../utils/imageLoader';
import { getProductsBinLocations } from '../../lib/services/binInventoryService';
import { formatBinLocationCompact, formatZoneCompact } from '../../utils/binLocationFormat';
import { getAdminId } from '../../utils/getAdminId';

interface PickListItem {
  sku: string;
  title: string;
  quantity: number;
  binLocations: ProductBinLocation[];
  imageUrl?: string | null;
  colorName?: string | null;
  sizeName?: string | null;
  picked: boolean;
}

// A line is "picked" when nothing remains to fulfill (already fulfilled in
// Shopify). remaining = fulfillable_quantity ?? quantity (null/legacy -> not picked).
const isLinePicked = (item: { quantity: number; fulfillable_quantity?: number }): boolean =>
  (item.fulfillable_quantity ?? item.quantity) <= 0;

interface ZoneGroup {
  zoneName: string;
  zoneCode: string;
  items: PickListItem[];
}

interface PickListPrintableProps {
  order: ShopifyOrder;
  onClose: () => void;
}

interface CompanyInfo {
  company_name?: string;
  avatar_data?: string;
}

const normalizeSku = (sku: string | null | undefined): string | null => {
  if (!sku) return null;
  return sku.toLowerCase().trim().replace(/\s+/g, ' ');
};

export const PickListPrintable: React.FC<PickListPrintableProps> = ({
  order,
  onClose,
}) => {
  const { user } = useAuth();
  const printRef = useRef<HTMLDivElement>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({});
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState<'data' | 'images'>('data');
  const [imageProgress, setImageProgress] = useState({ loaded: 0, total: 0 });
  const [zoneGroups, setZoneGroups] = useState<ZoneGroup[]>([]);
  const [unassignedItems, setUnassignedItems] = useState<PickListItem[]>([]);
  const [preloadedImages, setPreloadedImages] = useState<Map<string, string | null>>(new Map());

  const handleImageProgress = useCallback((loaded: number, total: number) => {
    setImageProgress({ loaded, total });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!user || !order.line_items || order.line_items.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadingStage('data');

      try {
        const adminId = getAdminId(user);

        const { data: userData } = await supabase
          .from('users')
          .select('company_name')
          .eq('id', adminId)
          .maybeSingle();

        const { data: settingsData } = await supabase
          .from('user_settings')
          .select('avatar_data')
          .eq('user_id', adminId)
          .maybeSingle();

        setCompanyInfo({
          company_name: userData?.company_name,
          avatar_data: settingsData?.avatar_data,
        });

        const originalSkus = order.line_items
          .map(item => item.sku)
          .filter((sku): sku is string => sku !== null && sku !== '');

        if (originalSkus.length === 0) {
          const items = order.line_items.map(item => ({
            sku: item.sku || '',
            title: item.title,
            quantity: item.quantity,
            binLocations: [],
            imageUrl: null,
            picked: isLinePicked(item),
          }));
          setUnassignedItems(items);
          setLoading(false);
          return;
        }

        const { data: products } = await supabase
          .from('products')
          .select('id, sku, image_url, color_id, size_id')
          .eq('admin_id', adminId)
          .in('sku', originalSkus);

        if (!products || products.length === 0) {
          const items = order.line_items.map(item => ({
            sku: item.sku || '',
            title: item.title,
            quantity: item.quantity,
            binLocations: [],
            imageUrl: null,
            picked: isLinePicked(item),
          }));
          setUnassignedItems(items);
          setLoading(false);
          return;
        }

        const colorIds = Array.from(
          new Set(products.map(p => p.color_id).filter((v): v is string => !!v)),
        );
        const sizeIds = Array.from(
          new Set(products.map(p => p.size_id).filter((v): v is string => !!v)),
        );
        const [colorsRes, sizesRes] = await Promise.all([
          colorIds.length > 0
            ? supabase.from('product_colors').select('id, name').in('id', colorIds)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
          sizeIds.length > 0
            ? supabase.from('product_sizes').select('id, name').in('id', sizeIds)
            : Promise.resolve({ data: [] as { id: string; name: string }[] }),
        ]);
        const colorById = new Map((colorsRes.data || []).map(c => [c.id, c.name]));
        const sizeById = new Map((sizesRes.data || []).map(s => [s.id, s.name]));

        const productIds = products.map(p => p.id);
        const skuToProductId = new Map<string, string>();
        const skuToImageUrl = new Map<string, string | null>();
        const skuToColor = new Map<string, string | null>();
        const skuToSize = new Map<string, string | null>();
        products.forEach(p => {
          const normalizedSku = normalizeSku(p.sku);
          if (normalizedSku) {
            skuToProductId.set(normalizedSku, p.id);
            skuToImageUrl.set(normalizedSku, p.image_url);
            skuToColor.set(normalizedSku, p.color_id ? colorById.get(p.color_id) ?? null : null);
            skuToSize.set(normalizedSku, p.size_id ? sizeById.get(p.size_id) ?? null : null);
          }
        });

        const binLocationsMap = await getProductsBinLocations(productIds, adminId);

        const locationMap = new Map<string, (ProductBinLocation & { zoneName?: string })[]>();

        for (const [productId, loc] of binLocationsMap) {
          const zoneName = loc.zone_code || 'Main Area';
          const entry: ProductBinLocation & { zoneName?: string } = {
            ...loc,
            zoneName,
          };
          const existing = locationMap.get(productId) || [];
          existing.push(entry);
          locationMap.set(productId, existing);
        }

        const zoneMap = new Map<string, PickListItem[]>();
        const unassigned: PickListItem[] = [];
        const allImageUrls: string[] = [];

        for (const item of order.line_items) {
          const skuKey = normalizeSku(item.sku);
          const productId = skuKey ? skuToProductId.get(skuKey) : null;
          const locations = productId ? locationMap.get(productId) || [] : [];
          const imageUrl = skuKey ? skuToImageUrl.get(skuKey) : null;
          const colorName = skuKey ? skuToColor.get(skuKey) ?? null : null;
          const sizeName = skuKey ? skuToSize.get(skuKey) ?? null : null;

          if (imageUrl) {
            allImageUrls.push(imageUrl);
          }

          const pickItem: PickListItem = {
            sku: item.sku || '',
            title: item.title,
            quantity: item.quantity,
            binLocations: locations,
            imageUrl: imageUrl || null,
            colorName,
            sizeName,
            picked: isLinePicked(item),
          };

          if (locations.length > 0) {
            const primaryLocation = locations[0];
            const zoneKey = primaryLocation.zone_code || 'main';
            if (!zoneMap.has(zoneKey)) {
              zoneMap.set(zoneKey, []);
            }
            zoneMap.get(zoneKey)!.push(pickItem);
          } else {
            unassigned.push(pickItem);
          }
        }

        const groups: ZoneGroup[] = [];
        for (const [zoneCode, items] of zoneMap) {
          const firstItemWithLocation = items.find(i => i.binLocations.length > 0);
          const zoneName = firstItemWithLocation?.binLocations[0]?.zone_code
            ? `Zone ${formatZoneCompact(firstItemWithLocation.binLocations[0].zone_code)}`
            : 'Main Area';

          items.sort((a, b) => {
            const aLoc = a.binLocations[0];
            const bLoc = b.binLocations[0];
            if (!aLoc || !bLoc) return 0;
            if (aLoc.rack_code !== bLoc.rack_code) {
              return aLoc.rack_code.localeCompare(bLoc.rack_code);
            }
            return aLoc.bin_code.localeCompare(bLoc.bin_code);
          });

          groups.push({ zoneName, zoneCode, items });
        }

        groups.sort((a, b) => a.zoneCode.localeCompare(b.zoneCode));

        setZoneGroups(groups);
        setUnassignedItems(unassigned);

        if (allImageUrls.length > 0) {
          setLoadingStage('images');
          setImageProgress({ loaded: 0, total: allImageUrls.length });
          const imageMap = await preloadPickListImages(allImageUrls, handleImageProgress);
          setPreloadedImages(imageMap);
        }

      } catch (err) {
        console.error('Error loading pick list data:', err);
        setZoneGroups([]);
        setUnassignedItems(order.line_items.map(item => ({
          sku: item.sku || '',
          title: item.title,
          quantity: item.quantity,
          binLocations: [],
          imageUrl: null,
          picked: isLinePicked(item),
        })));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, order.line_items, handleImageProgress]);

  const getImageSrc = useCallback((originalUrl: string | null | undefined): string | null => {
    if (!originalUrl) return null;
    const preloaded = preloadedImages.get(originalUrl);
    if (preloaded) return preloaded;
    return null;
  }, [preloadedImages]);

  const renderItemImage = (item: PickListItem) => {
    const imageSrc = getImageSrc(item.imageUrl);

    if (imageSrc) {
      return (
        <img
          src={imageSrc}
          alt={item.title}
          crossOrigin="anonymous"
          style={{
            width: '50px',
            height: '50px',
            objectFit: 'contain',
            borderRadius: '4px',
            border: '1px solid #e2e8f0',
            background: '#f8fafc'
          }}
        />
      );
    }

    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          background: '#f1f5f9',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
          fontSize: '10px',
          margin: '0 auto'
        }}
      >
        No Image
      </div>
    );
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Pick List - ${order.order_number}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1a1a1a; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .pick-list { max-width: 800px; margin: 0 auto; }
              .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #0891b2; }
              .logo img { max-height: 50px; width: auto; }
              .title { text-align: right; }
              .title h1 { font-size: 24px; color: #0891b2; margin-bottom: 4px; }
              .title p { color: #666; font-size: 14px; }
              .order-info { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; background: #f8fafc; padding: 16px; border-radius: 8px; }
              .info-item label { font-size: 10px; text-transform: uppercase; color: #666; letter-spacing: 0.5px; }
              .info-item p { font-weight: 600; font-size: 14px; margin-top: 4px; }
              .zone-section { margin-bottom: 24px; }
              .zone-header { background: linear-gradient(135deg, #0891b2, #0e7490); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0; display: flex; align-items: center; gap: 8px; }
              .zone-header h2 { font-size: 14px; font-weight: 700; }
              .zone-header span { background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 4px; font-size: 12px; }
              .pick-table { width: 100%; border-collapse: collapse; background: white; border: 1px solid #e2e8f0; border-top: none; }
              .pick-table th { background: #f1f5f9; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: #475569; font-weight: 600; border-bottom: 1px solid #e2e8f0; }
              .pick-table td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; }
              .pick-table tr:last-child td { border-bottom: none; }
              .bin-code { font-weight: 700; color: #0891b2; font-size: 14px; background: #f0f9ff; padding: 4px 8px; border-radius: 4px; display: inline-block; }
              .checkbox { width: 20px; height: 20px; border: 2px solid #cbd5e1; border-radius: 4px; }
              .qty { font-weight: 700; font-size: 16px; text-align: center; }
              .product-title { font-weight: 500; }
              .product-sku { color: #64748b; font-size: 11px; }
              .unassigned-section { margin-top: 24px; }
              .unassigned-header { background: #fef3c7; color: #92400e; padding: 10px 16px; border-radius: 8px 8px 0 0; display: flex; align-items: center; gap: 8px; }
              .unassigned-header h2 { font-size: 14px; font-weight: 700; }
              .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; color: #64748b; font-size: 11px; }
              .total-items { background: #0891b2; color: white; padding: 4px 12px; border-radius: 20px; font-weight: 600; }
              .product-image { width: 50px; height: 50px; object-fit: contain; border-radius: 4px; border: 1px solid #e2e8f0; background: #f8fafc; }
              .no-image { width: 50px; height: 50px; background: #f1f5f9; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 10px; margin: 0 auto; }
              @media print {
                body { padding: 15px; }
                .zone-section { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const totalItems = order.line_items.reduce((sum, item) => sum + item.quantity, 0);
  const pickedLineCount = order.line_items.filter(isLinePicked).length;
  const notPickedLineCount = order.line_items.length - pickedLineCount;
  const shippingAddress = order.shipping_address;

  const ROW_PICKED_BG = 'rgba(34, 197, 94, 0.12)';
  const ROW_NOT_PICKED_BG = 'rgba(234, 179, 8, 0.15)';
  const rowBg = (picked: boolean) => (picked ? ROW_PICKED_BG : ROW_NOT_PICKED_BG);

  const renderStatusBadge = (picked: boolean) => (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
        background: picked ? '#dcfce7' : '#fef9c3',
        color: picked ? '#166534' : '#854d0e',
        border: picked ? '1px solid #86efac' : '1px solid #fde047',
      }}
    >
      {picked ? 'Picked' : 'Not Picked'}
    </span>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center max-w-md w-full mx-4">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600 mx-auto mb-4" />
          {loadingStage === 'data' ? (
            <p className="text-gray-600 dark:text-gray-400">
              Loading pick list...
            </p>
          ) : (
            <div>
              <div className="flex items-center justify-center gap-2 mb-3">
                <ImageIcon className="h-5 w-5 text-cyan-600" />
                <p className="text-gray-600 dark:text-gray-400">
                  Loading product images...
                </p>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                <div
                  className="bg-cyan-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${imageProgress.total > 0 ? (imageProgress.loaded / imageProgress.total) * 100 : 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {imageProgress.loaded} / {imageProgress.total} images
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="min-h-screen py-8 px-4 flex items-start justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
                <ClipboardList className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Pick List - {order.order_number}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Grouped by warehouse zone for efficient picking
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-sm font-medium rounded-full flex items-center gap-1">
                <Package className="h-3.5 w-3.5" />
                {totalItems} items
              </span>
              {notPickedLineCount > 0 && (
                <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium rounded-full">
                  {notPickedLineCount} not picked
                </span>
              )}
              {pickedLineCount > 0 && (
                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium rounded-full">
                  {pickedLineCount} picked
                </span>
              )}
              <Button onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print Pick List
              </Button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div ref={printRef} className="p-8 bg-white">
            <div className="pick-list">
              <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '3px solid #0891b2' }}>
                <div className="logo">
                  {companyInfo.avatar_data ? (
                    <img src={companyInfo.avatar_data} alt="Logo" style={{ maxHeight: '50px', width: 'auto' }} />
                  ) : (
                    <div style={{ height: '50px', width: '100px' }}></div>
                  )}
                </div>
                <div className="title" style={{ textAlign: 'right' }}>
                  <h1 style={{ fontSize: '24px', color: '#0891b2', marginBottom: '4px' }}>PICK LIST</h1>
                  <p style={{ color: '#666', fontSize: '14px' }}>{order.order_number}</p>
                </div>
              </div>

              <div className="order-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px', background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                <div className="info-item">
                  <label style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666', letterSpacing: '0.5px' }}>Customer</label>
                  <p style={{ fontWeight: '600', fontSize: '14px', marginTop: '4px' }}>
                    {order.customer_name || `${order.customer_first_name} ${order.customer_last_name}`}
                  </p>
                </div>
                <div className="info-item">
                  <label style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666', letterSpacing: '0.5px' }}>Ship To</label>
                  <p style={{ fontWeight: '600', fontSize: '14px', marginTop: '4px' }}>
                    {shippingAddress ? `${shippingAddress.city}, ${shippingAddress.country}` : 'N/A'}
                  </p>
                </div>
                <div className="info-item">
                  <label style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666', letterSpacing: '0.5px' }}>Order Date</label>
                  <p style={{ fontWeight: '600', fontSize: '14px', marginTop: '4px' }}>
                    {new Date(order.order_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="info-item">
                  <label style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666', letterSpacing: '0.5px' }}>Total Items</label>
                  <p style={{ fontWeight: '600', fontSize: '14px', marginTop: '4px' }}>{totalItems}</p>
                </div>
              </div>

              {zoneGroups.map((group) => (
                <div key={group.zoneCode} className="zone-section" style={{ marginBottom: '24px' }}>
                  <div className="zone-header" style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)', color: 'white', padding: '10px 16px', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin style={{ width: '16px', height: '16px' }} />
                    <h2 style={{ fontSize: '14px', fontWeight: '700' }}>{group.zoneName}</h2>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                      {group.items.length} items
                    </span>
                  </div>
                  <table className="pick-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'white', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                    <thead>
                      <tr>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '90px' }}>
                          Status
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '100px' }}>
                          Bin Location
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>
                          Image
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>
                          Product
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '100px' }}>
                          SKU
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, idx) => (
                        <tr key={idx} style={{ backgroundColor: rowBg(item.picked) }}>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            {renderStatusBadge(item.picked)}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            {item.binLocations.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                {item.binLocations.slice(0, 1).map((loc, locIdx) => (
                                  <span key={locIdx} style={{ fontWeight: '700', color: '#0891b2', fontSize: '14px', background: '#f0f9ff', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                                    {formatBinLocationCompact(loc)}
                                    {item.binLocations.length > 1 && (
                                      <span style={{ marginLeft: '4px', fontSize: '10px', color: '#64748b' }}>+{item.binLocations.length - 1}</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontWeight: '500', color: '#92400e', fontSize: '12px' }}>N/A</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            {renderItemImage(item)}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ fontWeight: '500' }}>{item.title}</div>
                            {(item.colorName || item.sizeName) && (
                              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {item.colorName && (
                                  <span style={{ background: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Color: {item.colorName}
                                  </span>
                                )}
                                {item.sizeName && (
                                  <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Size: {item.sizeName}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '11px' }}>
                            {item.sku || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '700', fontSize: '16px' }}>
                            {item.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {unassignedItems.length > 0 && (
                <div className="unassigned-section" style={{ marginTop: '24px' }}>
                  <div className="unassigned-header" style={{ background: '#fef3c7', color: '#92400e', padding: '10px 16px', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Package style={{ width: '16px', height: '16px' }} />
                    <h2 style={{ fontSize: '14px', fontWeight: '700' }}>Unassigned Items</h2>
                    <span style={{ background: 'rgba(146,64,14,0.2)', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                      {unassignedItems.length} items
                    </span>
                  </div>
                  <table className="pick-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'white', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                    <thead>
                      <tr>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '90px' }}>
                          Status
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '100px' }}>
                          Bin Location
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>
                          Image
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>
                          Product
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '100px' }}>
                          SKU
                        </th>
                        <th style={{ background: '#f1f5f9', padding: '10px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#475569', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedItems.map((item, idx) => (
                        <tr key={idx} style={{ backgroundColor: rowBg(item.picked) }}>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            {renderStatusBadge(item.picked)}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            <span style={{ fontWeight: '500', color: '#92400e', fontSize: '12px' }}>N/A</span>
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                            {renderItemImage(item)}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ fontWeight: '500' }}>{item.title}</div>
                            {(item.colorName || item.sizeName) && (
                              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {item.colorName && (
                                  <span style={{ background: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Color: {item.colorName}
                                  </span>
                                )}
                                {item.sizeName && (
                                  <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Size: {item.sizeName}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '11px' }}>
                            {item.sku || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '700', fontSize: '16px' }}>
                            {item.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="footer" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '11px' }}>
                <span>Printed on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</span>
                <span style={{ background: '#0891b2', color: 'white', padding: '4px 12px', borderRadius: '20px', fontWeight: '600' }}>
                  Total: {totalItems} items
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print Pick List
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PickListPrintable;
